import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useBlocker } from 'react-router-dom';

import { vacancyFormActions } from '../../redux/slices/vacancyFormSlice';
import { useAppDispatch, useAppSelector, useHideWebsiteFooter, useIsMobile } from '../../../../../base/hooks';
import { __t } from '../../../../../base/utils/t';

import { Helmet } from '../../../../components/Helmet';
import { Header } from '../../../../../base/components/Header';
import { VacancyBaseInfoStep } from '../../components/VacancyBaseInfoStep';
import { VacancyRateInfoStep } from '../../components/VacancyRateInfoStep';
import { VacancyAdditionalInfoStep } from '../../components/VacancyAdditionalInfoStep';
import { PreviewModal } from '../../components/PreviewModal';
import { UnsavedChangesModal } from '../../components/UnsavedChangesModal';
import { VacancyLimitModal } from '../../components/VacancyLimitModal';
import { Footer } from '../../components/Footer';

import { isBypassBeforeUnload } from '../../utils/events';
import './styles.scss';

interface Props {
    duplicate?: boolean;
}

export const VacancyForm = ({ duplicate }: Props) => {
    const { id } = useParams();
    const {
        vacancyForm: { step, isDirty, showVacancyLimitModal },
        app: { referrer },
    } = useAppSelector((state) => state);
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    useHideWebsiteFooter();

    const [showPreview, setShowPreview] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const pendingHrefRef = useRef<string | null>(null);

    const isNewVacancy = (!!id && duplicate) || !id;

    useEffect(() => {
        return () => {
            dispatch(vacancyFormActions.refreshVacancyDataToInitialState());
        };
    }, []);

    useEffect(() => {
        dispatch(vacancyFormActions.init(id));
        if (duplicate) {
            dispatch(vacancyFormActions.setDuplicate(duplicate));
        }
    }, [id, duplicate]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [step]);

    // React Router navigation blocker
    const blocker = useBlocker(isNewVacancy && isDirty);

    useEffect(() => {
        if (blocker.state === 'blocked') {
            setShowUnsavedModal(true);
        }
    }, [blocker.state]);

    // Yii header link interceptor
    useEffect(() => {
        if (!isNewVacancy) return;

        const handleClick = (e: MouseEvent) => {
            if (!isDirty) return;
            if (e.defaultPrevented) return;
            const anchor = (e.target as Element).closest('a');
            if (!anchor) return;
            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('javascript:') || href === '#') return;
            e.preventDefault();
            pendingHrefRef.current = anchor.href;
            setShowUnsavedModal(true);
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [isNewVacancy, isDirty]);

    // beforeunload — browser close / address bar change (native browser prompt)
    // useEffect(() => {
    //     if (!isNewVacancy || !isDirty) return;
    //     const handler = (e: BeforeUnloadEvent) => {
    //         if (isBypassBeforeUnload()) return;
    //         e.preventDefault();
    //     };
    //     window.addEventListener('beforeunload', handler);
    //     return () => window.removeEventListener('beforeunload', handler);
    // }, [isNewVacancy, isDirty]);

    useEffect(() => {
        if (isMobile) {
            window.history.pushState(null, '', window.location.href);
            window.onpopstate = function () {
                window.history.go(1);
                if (step === 1) {
                    if (isNewVacancy && isDirty) {
                        setShowUnsavedModal(true);
                        return;
                    }
                    if (referrer) {
                        window.location.href = referrer;
                        return;
                    }
                    return;
                }

                dispatch(vacancyFormActions.setStep(step - 1));
            };
        }

        return () => {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            window.onpopstate = () => {};
        };
    }, [isMobile, step, referrer, isNewVacancy, isDirty]);

    const handleStay = useCallback(() => {
        blocker.reset?.();
        pendingHrefRef.current = null;
        setShowUnsavedModal(false);
    }, [blocker]);

    const handleConfirmLeave = useCallback(() => {
        if (blocker.state === 'blocked') {
            blocker.proceed?.();
        } else if (pendingHrefRef.current) {
            window.location.href = pendingHrefRef.current;
        } else {
            // mobile back case
            if (referrer) window.location.href = referrer;
            else window.history.back();
        }
        setShowUnsavedModal(false);
        pendingHrefRef.current = null;
    }, [blocker, referrer]);

    const onBackClick = useCallback(() => {
        if (step === 1) {
            if (referrer) {
                window.location.href = referrer;
                return;
            }
            return;
        }

        dispatch(vacancyFormActions.setStep(step - 1));
    }, [step, referrer]);

    const togglePreviewVisibility = () => setShowPreview(!showPreview);

    const pageTitle = useMemo(() => {
        if (window.GLOBALS.vacancyId && !window.GLOBALS.duplicate) {
            return __t('Редактирование вакансии');
        }
        return __t('Новая вакансия');
    }, []);

    const stepContent = useMemo(() => {
        switch (step) {
            case 1:
                return <VacancyBaseInfoStep />;
            case 2:
                return <VacancyRateInfoStep />;
            case 3:
                return (
                    <>
                        <VacancyAdditionalInfoStep previewClickHandler={togglePreviewVisibility} />
                        <PreviewModal visible={showPreview} visibilityHandler={togglePreviewVisibility} />
                    </>
                );
            default:
                return null;
        }
    }, [step, showPreview]);

    const pageName = useMemo(() => {
        if (duplicate) {
            return __t('Копирование вакансии');
        } else if (id) {
            return __t('Редактирование вакансии');
        } else {
            return __t('Новая вакансия');
        }
    }, [duplicate, id]);

    return (
        <>
            <Helmet pageName={pageName} />
            <div className='vacancy-form-page'>
                <Header title={pageTitle} onBackClick={onBackClick} />
                <div className='vacancy-form-page__form'>{stepContent}</div>
                <Footer previewClickHandler={togglePreviewVisibility} />
            </div>
            <UnsavedChangesModal
                visible={showUnsavedModal}
                visibilityHandler={handleStay}
                onConfirmLeave={handleConfirmLeave}
            />
            <VacancyLimitModal
                visible={showVacancyLimitModal}
                visibilityHandler={() => dispatch(vacancyFormActions.setShowVacancyLimitModal(false))}
            />
        </>
    );
};
